import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useTransition,
} from "react";

import { getCompanyLimitedStatsById } from "../../services/api/CasesApi";
import {
  UpdateHCFetchedManagedCases,
  UpdateHCAddedManagedCases,
  UpdateDCFetchedManagedCases,
  UpdateDCAddedManagedCases,
  UpdateOCAddedManagedCases,
  getDistrictCourtFetchedManageCases,
  getHighCourtFetchedManageCases,
  getOtherCourtAddedManageCases,
  getDistrictCourtAddedManageCases,
  getHighCourtAddedManageCases,
} from "../../services/api/CasesApi";
import {
  UpdateSCFetchedManagedCases,
  UpdateSCAddedManagedCases,
  getSupremeCourtFetchedManageCases,
  getSupremeCourtAddedManageCases,
} from "../../services/api/SuperAdminApi";
import "./CompanyModalLimit.scss";
import { truncatePartyNames } from "../TruncateParties";
import { FaSearch } from "react-icons/fa";
import gsap from "gsap";
import { useToast } from "../../context/ToastContext";
import { useCaseLimit } from "../../context/CaseLimitContext";
import PlanSelectionModal from "../PlanSelectionModal/PlanSelectionModal";
import ManageCaseRow from "./ManageCaseRow";

const parseFancyDate = (dateStr) => {
  if (!dateStr) return null;

  try {
    const cleanedDate = dateStr.replace(/(\d+)(st|nd|rd|th)/, "$1");
    const parsed = new Date(cleanedDate);
    if (isNaN(parsed.getTime())) return null;
    return parsed;
  } catch (error) {
    return null;
  }
};

const checkIsUndated = (caseItem) => {
  const rawDate = (
    caseItem.caseStatus?.["Next Hearing Date"] ||
    caseItem.customUndatedRows?.["Next Hearing Date"] ||
    ""
  )
    .toString()
    .trim();

  if (
    !rawDate ||
    rawDate === "" ||
    rawDate === "-" ||
    rawDate === "--" ||
    rawDate === "---" ||
    rawDate === "NA" ||
    rawDate === "N/A" ||
    rawDate === ":" ||
    rawDate === ":-"
  ) {
    return false;
  }

  const parsed = parseFancyDate(rawDate);
  if (!parsed) {
    return true;
  }

  const today = new Date();

  const parsedDateOnly = new Date(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate()
  );

  const todayDateOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  return parsedDateOnly < todayDateOnly;
};

const CompanyLimitModal = ({
  isOpen,
  onClose,
  selectedCompany = null,
  companyId = null,
  selectedRefs = new Set(),
  setSelectedRefs,
  onUpdateSuccess,
  prefetchedStats = null,
}) => {
  //const [stats, setStats] = useState(null);
  const [stats, setStats] = useState(prefetchedStats);
  const [loading, setLoading] = useState(isOpen && !prefetchedStats);
  // const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showToast } = useToast();
  const { refreshCaseLimitData } = useCaseLimit();

  const [isAllSelected, setIsAllSelected] = useState(false);

  // Manage Cases states
  const [activeTab, setActiveTab] = useState("high-court");
  const [activeSubTab, setActiveSubTab] = useState("fetched");
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [casesError, setCasesError] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [selectedCount, setSelectedCount] = useState(0);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const sasearchInputRef = useRef(null);
  const sasearchContainerRef = useRef(null);
  const searchButtonRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);
  const [sasearchQuery, setSearchQuery] = useState("");
  const [totalCasesForTab, setTotalCasesForTab] = useState(0);
  const [totalFilteredCases, setTotalFilteredCases] = useState(0);
  const [selectAllLoading, setSelectAllLoading] = useState(false); // Added for select all spinner

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const [pagination, setPagination] = useState({
    page: 0,
    limit: 25,
    totalCases: 0,
  });

  // Keyboard navigation state
  const [focusedIndex, setFocusedIndex] = useState(0);
  const caseRefs = useRef([]);
  const casesListRef = useRef(null);
  const userInteracting = useRef(false);
  useEffect(() => {
    caseRefs.current = [];
  }, [cases]);
  // Filter states
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    type: "all",
    filingDateFrom: "",
    filingDateTo: "",
    registrationDateFrom: "",
    registrationDateTo: "",
    firstHearingDateFrom: "",
    firstHearingDateTo: "",
    decisionDateFrom: "",
    decisionDateTo: "",
  });

  const [isStatsCollapsed, setIsStatsCollapsed] = useState(false);
  // PERFORMANCE OPTIMIZATION: Use transition for non-urgent updates
  const [isPending, startTransition] = useTransition();
  // PERFORMANCE OPTIMIZATION: Keep a ref to selectedRefs
  const selectedRefsRef = useRef(selectedRefs);
  useEffect(() => {
    selectedRefsRef.current = selectedRefs;
  }, [selectedRefs]);

  // PERFORMANCE OPTIMIZATION: Keep a ref to stats for case limit
  const caseLimitRef = useRef(0);
  useEffect(() => {
    caseLimitRef.current = stats?.limit?.caseLimit || 0;
  }, [stats?.limit?.caseLimit]);
  useEffect(() => {
    if (prefetchedStats) {
      setStats(prefetchedStats);
      setLoading(false); // <--- Crucial: Force loading to stop immediately
    }
  }, [prefetchedStats]);

  // NEW: Reset state when companyId changes to prevent stale data
  useEffect(() => {
    if (companyId) {
      setStats(prefetchedStats || null);
      setCases([]); // Clear cases immediately
      setCasesError(null);
      setError(null);
      // We don't necessarily reset tabs, but we could if needed
    }
  }, [companyId, prefetchedStats]);

  useEffect(() => {
    const list = casesListRef.current;
    if (!list) return;

    const handleScroll = () => {
      // Hysteresis: Collapse after 50px, Expand only when back at 0px
      // This prevents rapid flickering when hovering around the threshold
      const currentScroll = list.scrollTop;
      setIsStatsCollapsed((prev) => {
        if (!prev && currentScroll > 50) return true;
        if (prev && currentScroll === 0) return false;
        return prev;
      });
    };
    // Use passive listener for performance
    list.addEventListener("scroll", handleScroll, { passive: true });

    // Check initial position
    handleScroll();

    return () => {
      list.removeEventListener("scroll", handleScroll);
    };
  }, [casesLoading]); // Only re-attach if casesLoading changes (DOM might reset)

  useEffect(() => {
    if (!isOpen) {
      setInitialLoadComplete(false);
      setSelectedRefs(new Set());

      setFilters({
        status: "",
        type: "all",
        filingDateFrom: "",
        filingDateTo: "",
        registrationDateFrom: "",
        registrationDateTo: "",
        firstHearingDateFrom: "",
        firstHearingDateTo: "",
        decisionDateFrom: "",
        decisionDateTo: "",
      });
      setShowFilterDropdown(false);

      setSearchQuery("");
      setIsSearching(false);
      setShowSearchInput(false);

      
      setPagination((prev) => ({
        ...prev,
        page: 0,
      }));
    }
  }, [isOpen, setSelectedRefs]);

  useEffect(() => {
    const fetchAllSelectedCases = async () => {
      if (!isOpen || !companyId) return;

      setInitialLoadComplete(false);
      setSelectedRefs(new Set());

      try {
        const [hcFetched, hcAdded, dcFetched, dcAdded, ocAdded, scFetched, scAdded] = await Promise.all([
          getHighCourtFetchedManageCases(companyId),
          getHighCourtAddedManageCases(companyId),
          getDistrictCourtFetchedManageCases(companyId),
          getDistrictCourtAddedManageCases(companyId),
          getOtherCourtAddedManageCases(companyId),
          getSupremeCourtFetchedManageCases(companyId),
          getSupremeCourtAddedManageCases(companyId),
        ]);

        const allSelectedRefs = new Set();

        [hcFetched, hcAdded, dcFetched, dcAdded, ocAdded, scFetched, scAdded].forEach(response => {
          const cases = response?.data?.cases || [];
          cases.forEach(caseItem => {
            if (caseItem.isChecked === true) {
              const ref = caseItem.viewDetails?.courtRef;
              if (ref) {
                allSelectedRefs.add(ref);
              }
            }
          });
        });

        setSelectedRefs(allSelectedRefs);
        setInitialLoadComplete(true);
      } catch (err) {
        console.error("Error fetching all selected cases:", err);
        setInitialLoadComplete(true);
      }
    };

    fetchAllSelectedCases();
  }, [isOpen, companyId, setSelectedRefs]);

  useEffect(() => {
    setSelectedCount(selectedRefs.size);
  }, [selectedRefs]);

  useEffect(() => {
    if (cases.length === 0) {
      setIsAllSelected(false);
    } else {
      const allChecked = cases.every((item) =>
        selectedRefs.has(item.viewDetails?.courtRef)
      );
      setIsAllSelected(allChecked);
    }
  }, [cases, selectedRefs]);

  useEffect(() => {
    setSearchQuery("");
    setIsSearching(false);
    setShowSearchInput(false);
    setFilters({
      status: "",
      type: "all",
      filingDateFrom: "",
      filingDateTo: "",
      registrationDateFrom: "",
      registrationDateTo: "",
      firstHearingDateFrom: "",
      firstHearingDateTo: "",
      decisionDateFrom: "",
      decisionDateTo: "",
    });
    setShowFilterDropdown(false);
  }, [activeTab, activeSubTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && !event.target.closest('.company-modal-filter-dropdown') && !event.target.closest('.filter-btn')) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);

  const scrollToFocusedCase = useCallback((index) => {
    if (caseRefs.current[index]) {
      caseRefs.current[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!companyId) {
        throw new Error("Company ID is required to fetch statistics");
      }

      const response = await getCompanyLimitedStatsById(companyId);
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching company stats:", err);
      setError(err.message || "Failed to load company statistics");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const handleSelectAll = () => {
    const currentSystemTotal = stats?.cases?.totalCases || 0;
    const caseLimit = stats?.limit?.caseLimit || 0;

    const selectableCases = cases.filter(
      (c) => !c.assignedTo || c.assignedTo.length === 0
    );

    const refsOnPage = selectableCases
      .map((c) => c.viewDetails?.courtRef)
      .filter(Boolean);

    if (refsOnPage.length === 0) {
      showToast(
        "No selectable cases on this page (all cases are assigned)",
        "warning"
      );

      return;
    }

    const areAllOnPageSelected =
      refsOnPage.length > 0 && refsOnPage.every((ref) => selectedRefs.has(ref));

    if (!areAllOnPageSelected) {
      const newRefsToAdd = refsOnPage.filter((ref) => !selectedRefs.has(ref));

      if (currentSystemTotal + newRefsToAdd.length > caseLimit) {
        setShowLimitModal(true);
        return;
      }
    }

    setSelectedRefs((prevSelectedRefs) => {
      const newSelectedRefs = new Set(prevSelectedRefs);
      if (areAllOnPageSelected) {
        refsOnPage.forEach((ref) => newSelectedRefs.delete(ref));
      } else {
        refsOnPage.forEach((ref) => newSelectedRefs.add(ref));
      }
      return newSelectedRefs;
    });
  };

  const buildQueryParams = useCallback((options = { includePagination: true }) => {
    const params = new URLSearchParams();

    // Only add pagination if requested (default: true)
    if (options.includePagination && pagination.limit !== "all") {
      params.set("page", pagination.page.toString());
      params.set("limit", pagination.limit.toString());
    }

    if (sasearchQuery && sasearchQuery.trim()) {
      params.set("search", sasearchQuery.trim());
    }

    if (filters.status && filters.status !== "") {
      params.set("status", filters.status);
    }

      if (filters.type && filters.type !== "all") {
        params.set("type", filters.type);
      }

    if (filters.filingDateFrom) params.set("filingDateFrom", filters.filingDateFrom);
    if (filters.filingDateTo) params.set("filingDateTo", filters.filingDateTo);
    if (filters.registrationDateFrom) params.set("registrationDateFrom", filters.registrationDateFrom);
    if (filters.registrationDateTo) params.set("registrationDateTo", filters.registrationDateTo);
    if (filters.firstHearingDateFrom) params.set("firstHearingDateFrom", filters.firstHearingDateFrom);
    if (filters.firstHearingDateTo) params.set("firstHearingDateTo", filters.firstHearingDateTo);
    if (filters.decisionDateFrom) params.set("decisionDateFrom", filters.decisionDateFrom);
    if (filters.decisionDateTo) params.set("decisionDateTo", filters.decisionDateTo);

      return params.toString();
    },
    [sasearchQuery, filters, pagination.page, pagination.limit]
  );

  const fetchCases = useCallback(async () => {
    setCasesLoading(true);
    setCasesError(null);
    try {
      let response;
      if (!companyId) throw new Error("Company ID is required to fetch cases");

      const queryParams = buildQueryParams({ includePagination: true });
      const queryString = queryParams ? `?${queryParams}` : "";

      if (activeTab === "high-court") {
        response =
          activeSubTab === "fetched"
            ? await getHighCourtFetchedManageCases(companyId, queryString)
            : await getHighCourtAddedManageCases(companyId, queryString);
      } else if (activeTab === "district-court") {
        response =
          activeSubTab === "fetched"
            ? await getDistrictCourtFetchedManageCases(companyId, queryString)
            : await getDistrictCourtAddedManageCases(companyId, queryString);
      } else if (activeTab === "other-court") {
        response = await getOtherCourtAddedManageCases(companyId, queryString);
      } else if (activeTab === "supreme-court") {
        response =
          activeSubTab === "fetched"
            ? await getSupremeCourtFetchedManageCases(companyId, queryString)
            : await getSupremeCourtAddedManageCases(companyId, queryString);
      }

      const casesData = response?.data?.cases || [];

      setCases(casesData);

      const effectiveTotal =
        response?.data?.totalFilteredCases !== undefined &&
        response?.data?.totalFilteredCases !== null
          ? response?.data?.totalFilteredCases
          : response?.data?.totalCases;

      setPagination((prev) => ({
        ...prev,

        totalCases: effectiveTotal || 0,
      }));

      // setPagination(prev => ({ ...prev, totalCases: response?.data?.totalCases || 0 }));

      setTotalCasesForTab(response?.data.totalCases || 0);

      setTotalFilteredCases(response?.data?.totalFilteredCases || 0);
    } catch (err) {
      console.error("Error fetching cases:", err);

      setCasesError(err.message || "Failed to load cases");
    } finally {
      setCasesLoading(false);
    }
  }, [companyId, activeTab, activeSubTab, buildQueryParams]);

  const handlePageChange = useCallback((newPage) => {
    setCases([]);

    setPagination((prev) => ({
      ...prev,

      page: newPage,
    }));
    userInteracting.current = false;
  }, []);

  const handleLimitChange = useCallback((e) => {
    const newLimit = e.target.value === "all" ? "all" : Number(e.target.value);

    setPagination((prev) => ({
      ...prev,
      limit: newLimit,

      page: 0,
    }));
  }, []);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,

      page: 0,
    }));

    userInteracting.current = false;
  }, [activeTab, activeSubTab]);

  useEffect(() => {
    // UPDATED: Remove !stats check to allow fetching when companyId changes

    // This ensures we always get fresh stats for the current companyId
    if (isOpen && companyId && !prefetchedStats) {
      fetchStats();
    }
  }, [isOpen, companyId, fetchStats, prefetchedStats]);

  useEffect(() => {
    if (isOpen && companyId) {
      const timeoutId = setTimeout(() => {
        fetchCases();
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [
    filters,
    sasearchQuery,
    pagination.page,
    pagination.limit,
    fetchCases,
    isOpen,
    companyId,
  ]);

  const renderPaginationControls = () => {
    const totalPages =
      pagination.limit === "all"
        ? 1
        : Math.ceil(pagination.totalCases / pagination.limit);

    if (pagination.totalCases === 0) return null;

    return (
      <div className="manage-cases-pagination-compact">
        <div className="pagination-left">
          <select
            value={pagination.limit}
            onChange={handleLimitChange}
            className="pagination-select"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
            <option value="all">All</option>
          </select>

          <div className="pagination-nav">
            <button
              disabled={pagination.page === 0 || pagination.limit === "all"}
              onClick={() => handlePageChange(0)}
              className="pagination-btn-compact"
              title="First Page"
            >
              ⟪
            </button>
            <button
              disabled={pagination.page === 0 || pagination.limit === "all"}
              onClick={() => handlePageChange(pagination.page - 1)}
              className="pagination-btn-compact"
              title="Previous Page"
            >
              ‹
            </button>
            <span className="pagination-info-compact">
              {pagination.page + 1}/{totalPages}
            </span>
            <button
              disabled={
                pagination.page >= totalPages - 1 || pagination.limit === "all"
              }
              onClick={() => handlePageChange(pagination.page + 1)}
              className="pagination-btn-compact"
              title="Next Page"
            >
              ›
            </button>
            <button
              disabled={
                pagination.page >= totalPages - 1 || pagination.limit === "all"
              }
              onClick={() => handlePageChange(totalPages - 1)}
              className="pagination-btn-compact"
              title="Last Page"
            >
              ⟫
            </button>
          </div>
        </div>

        <span className="pagination-total-compact">
          {pagination.totalCases} total
        </span>
      </div>
    );
  };

  // const handleFilterChange = (field, value) => {

  //   const newFilters = { ...filters, [field]: value };
  //   setFilters(newFilters);
  // };
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    // Add this line to sync pagination with filters
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const hasActiveFilters = Object.values(filters).some(
    (value) => value && value !== "" && value !== "all"
  );

  const clearFilters = () => {
    setFilters({
      status: "",
      type: "all",
      filingDateFrom: "",
      filingDateTo: "",
      registrationDateFrom: "",
      registrationDateTo: "",
      firstHearingDateFrom: "",
      firstHearingDateTo: "",
      decisionDateFrom: "",
      decisionDateTo: "",
    });
  };

  // const handleSearchInputChange = (e) => {

  //   const query = e.target.value;

  //   setSearchQuery(query);

  // };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;

    setSearchQuery(query);

    // Add this line to sync pagination with search

    setPagination((prev) => ({ ...prev, page: 0 }));
  };


  const handleCaseCheck = useCallback(
    (caseItem) => {
      const courtRef = caseItem.viewDetails?.courtRef;

      if (!courtRef) return;

      if (caseItem.assignedTo && caseItem.assignedTo.length > 0) {
        showToast(
          "Cannot modify cases that are already assigned to users",
          "warning"
        );

        return;
      }

      setSelectedRefs((prevSelectedRefs) => {
        const isCurrentlySelected = prevSelectedRefs.has(courtRef);

        const currentSelectionCount = prevSelectedRefs.size;

        const caseLimit = caseLimitRef.current;

        if (!isCurrentlySelected && currentSelectionCount >= caseLimit) {
          // Use setTimeout to avoid state update during render

          setTimeout(() => setShowLimitModal(true), 0);

          return prevSelectedRefs;
        }

        const newSelectedRefs = new Set(prevSelectedRefs);

        if (isCurrentlySelected) {
          newSelectedRefs.delete(courtRef);
        } else {
          newSelectedRefs.add(courtRef);
        }

        return newSelectedRefs;
      });
    },
    [showToast, setSelectedRefs]
  );

  const handleSelectPlan = (selectedPlan, selectedCompanyId, paymentData) => {
    // Payment is handled in PlanSelectionModal
  };

  const handleSearchToggle = () => {
    if (!showSearchInput) {
      setShowSearchInput(true);

      gsap.fromTo(
        sasearchContainerRef.current,

        {
          width: 0,

          opacity: 0,

          overflow: "hidden",
        },

        {
          width: "200px",

          opacity: 1,

          duration: 0.3,

          ease: "power2.out",

          onComplete: () => {
            if (sasearchInputRef.current) {
              sasearchInputRef.current.focus();
            }
          },
        }
      );
    } else {
      handleClearSearch();
    }
  };

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");

    setIsSearching(false);

    gsap.to(sasearchContainerRef.current, {
      width: 0,

      opacity: 0,

      duration: 0.3,

      ease: "power2.in",

      onComplete: () => {
        setShowSearchInput(false);
      },
    });
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === "Escape") {
      handleClearSearch();
    }
  };

  useEffect(() => {
    if (showSearchInput) {
      setSearchQuery("");

      setIsSearching(false);
    }
  }, [activeTab, showSearchInput]);

  // OPTIMIZED: Memoized SelectionCounter component

  const SelectionCounter = useMemo(() => {
    // 1. Calculate counts based on currently visible filtered cases

    const currentPageSelectedCount = cases.filter((c) =>
      selectedRefs.has(c.viewDetails?.courtRef)
    ).length;

    // refsOnPage only includes cases currently visible (filtered)

    const refsOnPage = cases
      .map((c) => c.viewDetails?.courtRef)
      .filter(Boolean);

    // 2. Logic for "Select Current Tab" (TAB-SPECIFIC)

    const handleSelectCurrentTab = async () => {
      setSelectAllLoading(true);

      try {
        // Build Query String (filters + no pagination limits)

        const filterQueryParams = buildQueryParams({
          includePagination: false,
        });

        const queryString = filterQueryParams
          ? `?limit=100000&${filterQueryParams}`
          : "?limit=100000";

        let response;

        // Fetch ONLY the sub-tab we are currently viewing

        if (activeTab === "high-court") {
          response =
            activeSubTab === "fetched"
              ? await getHighCourtFetchedManageCases(companyId, queryString)
              : await getHighCourtAddedManageCases(companyId, queryString);
        } else if (activeTab === "district-court") {
          response =
            activeSubTab === "fetched"
              ? await getDistrictCourtFetchedManageCases(companyId, queryString)
              : await getDistrictCourtAddedManageCases(companyId, queryString);
        } else if (activeTab === "other-court") {
          response = await getOtherCourtAddedManageCases(
            companyId,
            queryString
          );
        } else if (activeTab === "supreme-court") {
          response =
            activeSubTab === "fetched"
              ? await getSupremeCourtFetchedManageCases(companyId, queryString)
              : await getSupremeCourtAddedManageCases(companyId, queryString);
        }

        const fetchedCases = response?.data?.cases || [];

        const newSet = new Set(selectedRefs);

        let addedCount = 0;

        // Add IDs to the Set

        fetchedCases.forEach((caseItem) => {
          // Only add if not assigned

          if (!caseItem.assignedTo || caseItem.assignedTo.length === 0) {
            const ref = caseItem.viewDetails?.courtRef;

            if (ref) {
              if (!newSet.has(ref)) {
                newSet.add(ref);

                addedCount++;
              }
            }
          }
        });

        // Check Limit BEFORE updating state

        const caseLimit = stats?.limit?.caseLimit || 0;

        if (newSet.size > caseLimit) {
          setShowLimitModal(true);

          setSelectAllLoading(false);

          return;
        }

        startTransition(() => {
          setSelectedRefs(newSet);
        });

        // Nice Toast Message

        const tabName =
          activeTab === "other-court"
            ? "Other Courts"
            : `${activeTab
                .replace("-", " ")
                .toUpperCase()} (${activeSubTab.toUpperCase()})`;

        if (addedCount === 0) {
          showToast(`All cases in ${tabName} are already selected`, "info");
        } else {
          const filterMsg =
            sasearchQuery || hasActiveFilters ? "matching filters" : "";

          showToast(`Selected cases in ${tabName} ${filterMsg}`, "success");
        }
      } catch (err) {
        console.error("Error selecting tab cases:", err);

        showToast("Failed to select cases", "error");
      } finally {
        setSelectAllLoading(false);
      }
    };

    // 3. Logic for "Clear Current Tab" (TAB-SPECIFIC)

    const handleClearCurrentTab = async () => {
      setSelectAllLoading(true);

      try {
        const filterQueryParams = buildQueryParams({
          includePagination: false,
        });

        const queryString = filterQueryParams
          ? `?limit=100000&${filterQueryParams}`
          : "?limit=100000";

        let response;

        if (activeTab === "high-court") {
          response =
            activeSubTab === "fetched"
              ? await getHighCourtFetchedManageCases(companyId, queryString)
              : await getHighCourtAddedManageCases(companyId, queryString);
        } else if (activeTab === "district-court") {
          response =
            activeSubTab === "fetched"
              ? await getDistrictCourtFetchedManageCases(companyId, queryString)
              : await getDistrictCourtAddedManageCases(companyId, queryString);
        } else if (activeTab === "other-court") {
          response = await getOtherCourtAddedManageCases(
            companyId,
            queryString
          );
        } else if (activeTab === "supreme-court") {
          response =
            activeSubTab === "fetched"
              ? await getSupremeCourtFetchedManageCases(companyId, queryString)
              : await getSupremeCourtAddedManageCases(companyId, queryString);
        }

        let removedCount = 0;

        startTransition(() => {
          setSelectedRefs((prevSelectedRefs) => {
            const newSelectedRefs = new Set(prevSelectedRefs);

            const fetchedCases = response?.data?.cases || [];

            fetchedCases.forEach((caseItem) => {
              const ref = caseItem.viewDetails?.courtRef;

              if (ref && newSelectedRefs.has(ref)) {
                newSelectedRefs.delete(ref);

                removedCount++;
              }
            });

            return newSelectedRefs;
          });
        });

        const tabName =
          activeTab === "other-court"
            ? "Other Courts"
            : `${activeTab
                .replace("-", " ")
                .toUpperCase()} (${activeSubTab.toUpperCase()})`;

        if (removedCount > 0) {
          showToast(
            `Cleared ${removedCount} selections in ${tabName}`,
            "success"
          );
        } else {
          showToast(`No selections found to clear in ${tabName}`, "info");
        }
      } catch (err) {
        console.error("Error clearing tab:", err);

        showToast("Failed to clear cases", "error");
      } finally {
        setSelectAllLoading(false);
      }
    };

    // 4. Logic for "Clear Page" button (PAGE-SPECIFIC)

    const handleClearPage = () => {
      startTransition(() => {
        setSelectedRefs((prevSelectedRefs) => {
          const newSelectedRefs = new Set(prevSelectedRefs);

          // Only remove cases that are currently visible on this filtered page

          refsOnPage.forEach((ref) => newSelectedRefs.delete(ref));

          return newSelectedRefs;
        });
      });

      showToast(
        `Cleared ${currentPageSelectedCount} cases from current page`,
        "success"
      );
    };

    const hasSelectionsOnPage = currentPageSelectedCount > 0;

    return (
      <div className="selection-counter">
        <div className="selection-stats-text">
          <span className="selection-count">
            {selectedRefs.size} of {stats?.limit?.caseLimit || 0} total cases
            selected (system-wide)
          </span>

          <span className="tab-selection-count">
            ({currentPageSelectedCount} selected on current page)
          </span>

          {sasearchQuery.trim() || hasActiveFilters ? (
            <span className="search-info">
              | Filtered: {totalFilteredCases} (Total in tab: {totalCasesForTab}
              )
            </span>
          ) : (
            <span className="selection-count">
              {" "}
              | Total in tab: {totalCasesForTab}
            </span>
          )}
        </div>

        <div className="selection-actions">
          <div className="select-all-wrapper">
            <input
              type="checkbox"
              id="select-all-checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
              className="select-all-checkbox"
            />

            <label htmlFor="select-all-checkbox" className="select-all-label">
              Select Page
            </label>
          </div>

          <button
            className="select-all-cases-btn"
            onClick={handleSelectCurrentTab}
            disabled={selectAllLoading}
            title={
              sasearchQuery || hasActiveFilters
                ? `Select cases in ${activeTab} matching current filters`
                : `Select all cases in ${activeTab} only`
            }
          >
            {selectAllLoading
              ? "Processing..."
              : sasearchQuery || hasActiveFilters
              ? "Select Tab (Filtered)"
              : "Select Tab"}
          </button>

          {hasSelectionsOnPage && (
            <button
              className="clear-page-btn"
              onClick={handleClearPage}
              title="Clear selections on current page only"
            >
              Clear Page
            </button>
          )}

          {selectedRefs.size > 0 && (
            <button
              className="clear-selection-btn"
              onClick={handleClearCurrentTab}
              disabled={selectAllLoading}
              title={
                sasearchQuery || hasActiveFilters
                  ? `Deselect cases in ${activeTab} matching current filters`
                  : `Deselect all cases in ${activeTab} only`
              }
            >
              {sasearchQuery || hasActiveFilters
                ? "Clear Tab (Filtered)"
                : "Clear Tab"}
            </button>
          )}
        </div>
      </div>
    );
  }, [
    cases,

    selectedRefs,

    stats?.limit?.caseLimit,

    sasearchQuery,

    hasActiveFilters,

    totalFilteredCases,

    totalCasesForTab,

    isAllSelected,

    handleSelectAll,

    companyId,

    setSelectedRefs,

    showToast,

    buildQueryParams,

    selectAllLoading,

    activeTab,

    activeSubTab,
  ]);

  const renderLimitExceededModal = () => {
    if (!showLimitModal) return null;

    return (
      <div
        className="limit-modal-overlay"
        onClick={() => setShowLimitModal(false)}
      >
        <div
          className="limit-modal-container"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="limit-modal-header">
            <h4>Selection Limit Exceeded</h4>

            <button
              className="limit-modal-close"
              onClick={() => setShowLimitModal(false)}
            >
              ×
            </button>
          </div>

          <div className="limit-modal-content">
            <p>
              You have reached your total case limit of{" "}
              <strong>{stats?.limit?.caseLimit || 0}</strong> cases.
            </p>

            <p>
              Currently you have <strong>{selectedRefs.size}</strong> cases
              selected across all court types.
            </p>

            <p>
              Please deselect some cases from any tab before selecting new ones,
              or upgrade your plan to manage more cases.
            </p>
          </div>

          <div className="limit-modal-footer">
            <button
              className="limit-modal-upgrade-btn"
              onClick={() => {
                setShowLimitModal(false);

                setIsPlanModalOpen(true);
              }}
            >
              Upgrade Now
            </button>

            <button
              className="limit-modal-btn"
              onClick={() => setShowLimitModal(false)}
            >
              Understood
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getUpdateButtonText = () => {
    if (activeTab === "high-court") {
      return "Add/Remove HC Cases";
    } else if (activeTab === "district-court") {
      return "Add/Remove DC Cases";
    } else if (activeTab === "supreme-court") {
      return "Add/Remove SC Cases";
    } else {
      return "Add/Remove Other Court Cases";
    }
  };

  const handleUpdateCases = useCallback(async () => {
    const caseLimit = stats?.limit?.caseLimit || 0;

    const selectedCasesCount = selectedRefs.size;

    if (selectedCasesCount > caseLimit) {
      setShowLimitModal(true);

      return;
    }

    const payload = {
      courtRefs: Array.from(selectedRefs),
    };

    setUpdateLoading(true);

    try {
      let response;

      if (activeTab === "high-court") {
        if (activeSubTab === "fetched") {
          response = await UpdateHCFetchedManagedCases(companyId, payload);
        } else {
          response = await UpdateHCAddedManagedCases(companyId, payload);
        }
      } else if (activeTab === "district-court") {
        if (activeSubTab === "fetched") {
          response = await UpdateDCFetchedManagedCases(companyId, payload);
        } else {
          response = await UpdateDCAddedManagedCases(companyId, payload);
        }
      } else if (activeTab === "other-court") {
        response = await UpdateOCAddedManagedCases(companyId, payload);
      } else if (activeTab === "supreme-court") {
        if (activeSubTab === "fetched") {
          response = await UpdateSCFetchedManagedCases(companyId, payload);
        } else {
          response = await UpdateSCAddedManagedCases(companyId, payload);
        }
      }

      await Promise.all([fetchCases(), fetchStats()]);

      await refreshCaseLimitData();

      showToast("Cases updated successfully", "success");

      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
    } catch (err) {
      console.error("Error updating cases:", err);

      showToast("Failed to update cases. Please try again.", "error");
    } finally {
      setUpdateLoading(false);
    }
  }, [
    stats,
    selectedRefs,
    activeTab,
    activeSubTab,
    companyId,
    fetchCases,
    fetchStats,
    showToast,
    onUpdateSuccess,
    refreshCaseLimitData,
  ]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen || cases.length === 0) return;

      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      userInteracting.current = true;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();

          setFocusedIndex((prev) => {
            const newIndex = Math.min(prev + 1, cases.length - 1);

            scrollToFocusedCase(newIndex);

            return newIndex;
          });

          break;

        case "ArrowUp":
          event.preventDefault();

          setFocusedIndex((prev) => {
            const newIndex = Math.max(prev - 1, 0);

            scrollToFocusedCase(newIndex);

            return newIndex;
          });

          break;

        case " ":
          event.preventDefault();

          if (cases[focusedIndex]) {
            const focusedCase = cases[focusedIndex];

            const isAssigned =
              focusedCase.assignedTo && focusedCase.assignedTo.length > 0;

            if (!isAssigned) {
              handleCaseCheck(focusedCase);
            } else {
              showToast(
                "Cannot modify cases that are already assigned to users",
                "warning"
              );
            }
          }

          break;

        case "Enter":
          event.preventDefault();

          handleUpdateCases();

          break;

        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isOpen,
    cases,
    focusedIndex,
    handleCaseCheck,
    handleUpdateCases,
    scrollToFocusedCase,
    showToast,
  ]);

  if (!isOpen) return null;

  const renderStatsContent = () => {
    if (loading) {
      return (
        <div className="compact-modal-loading">
          <div className="compact-spinner"></div>

          <p>Loading...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="compact-modal-error">
          <p>{error}</p>

          <button onClick={fetchStats}>Retry</button>
        </div>
      );
    }

    if (stats) {
      const usersProgress = (stats.users.total / stats.limit.usersLimit) * 100;

      const casesProgress =
        (stats.cases.totalCases / stats.limit.caseLimit) * 100;

      const highCourtPercentage =
        stats.cases.totalCases > 0
          ? (
              (stats.cases.totalHighCourtCases / stats.cases.totalCases) *
              100
            ).toFixed(1)
          : 0;

      const districtCourtPercentage =
        stats.cases.totalCases > 0
          ? (
              (stats.cases.totalDistrictCourtCases / stats.cases.totalCases) *
              100
            ).toFixed(1)
          : 0;

      const otherCourtPercentage =
        stats.cases.totalCases > 0
          ? (
              (stats.cases.totalOtherCourtCases / stats.cases.totalCases) *
              100
            ).toFixed(1)
          : 0;

      return (
        <div
          className={`compact-modal-content ${
            isStatsCollapsed ? "collapsed" : ""
          }`}
        >
          <div className="image-style-stats-container">
            {/* <div className="image-style-stat-card users-card">

              <div className="head-img">

                <div className="stat-icon">

                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">

                    <path

                      d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"

                      fill="currentColor"

                    />

                    <path

                      d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z"

                      fill="currentColor"

                    />

                  </svg>

                </div>

                <div className="stat-label">Users</div>

              </div>

              <div className="stat-content">

                <div className="stat-value number-value-stat-comp">

                  {stats.users.total.toLocaleString()} /{" "}

                  {stats.limit.usersLimit.toLocaleString()}

                </div>

                <div className="stat-progress-bar">

                  <div

                    className="stat-progress-fill users-progress"

                    style={{ width: `${usersProgress}%` }}

                  ></div>

                </div>

                <div className="stat-percentage">

                  {usersProgress.toFixed(1)}% used

                </div>

              </div>

            </div> */}

            <div className="image-style-stat-card cases-card">
              <div className="head-img">
                <div className="stat-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M7 4V2C7 1.44772 7.44772 1 8 1H16C16.5523 1 17 1.44772 17 2V4H20C20.5523 4 21 4.44772 21 5C21 5.55228 20.5523 6 20 6H19V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6H4C3.44772 6 3 5.55228 3 5C3 4.44772 3.44772 4 4 4H7ZM9 3V4H15V3H9ZM7 6V20H17V6H7Z"
                      fill="currentColor"
                    />

                    <path d="M9 8H15V10H9V8Z" fill="currentColor" />

                    <path d="M9 12H15V14H9V12Z" fill="currentColor" />
                  </svg>
                </div>

                <div className="stat-label">Total Cases</div>
              </div>

              <div className="stat-content">
                <div className="stat-value number-value-stat-comp">
                  {stats.cases.totalCases.toLocaleString()} /{" "}
                  {stats.limit.caseLimit.toLocaleString()}
                </div>

                <div className="stat-progress-bar">
                  <div
                    className="stat-progress-fill cases-progress"
                    style={{ width: `${casesProgress}%` }}
                  ></div>
                </div>

                <div className="stat-percentage">
                  {casesProgress.toFixed(1)}% used
                </div>
              </div>
            </div>

            <div className="image-style-stat-card highcourt-card">
              <div className="head-img">
                <div className="stat-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 2L3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6L18 2H6Z"
                      fill="currentColor"
                    />

                    <path d="M3 6H21" stroke="white" strokeWidth="2" />

                    <path d="M8 11V17" stroke="white" strokeWidth="1.5" />

                    <path d="M12 11V17" stroke="white" strokeWidth="1.5" />

                    <path d="M16 11V17" stroke="white" strokeWidth="1.5" />
                  </svg>
                </div>

                <div className="stat-label">High Court</div>
              </div>

              <div className="stat-content">
                <div className="stat-value number-value-stat-comp">
                  {stats.cases.totalHighCourtCases.toLocaleString()}
                </div>
              </div>
            </div>

            <div className="image-style-stat-card districtcourt-card">
              <div className="head-img">
                <div className="stat-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 3H21V7H3V3Z" fill="currentColor" />

                    <path d="M3 17H21V21H3V17Z" fill="currentColor" />

                    <path d="M6 7V17H8V7H6Z" fill="currentColor" />

                    <path d="M11 7V17H13V7H11Z" fill="currentColor" />

                    <path d="M16 7V17H18V7H16Z" fill="currentColor" />
                  </svg>
                </div>

                <div className="stat-label">District Court</div>
              </div>

              <div className="stat-content">
                <div className="stat-value number-value-stat-comp">
                  {stats.cases.totalDistrictCourtCases.toLocaleString()}
                </div>
              </div>
            </div>

            <div className="image-style-stat-card othercourts-card">
              <div className="head-img">
                <div className="stat-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M7 4V2C7 1.44772 7.44772 1 8 1H16C16.5523 1 17 1.44772 17 2V4H20C20.5523 4 21 4.44772 21 5C21 5.55228 20.5523 6 20 6H19V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6H4C3.44772 6 3 5.55228 3 5C3 4.44772 3.44772 4 4 4H7ZM9 3V4H15V3H9ZM7 6V20H17V6H7Z"
                      fill="currentColor"
                    />

                    <circle cx="12" cy="12" r="3" fill="white" />
                  </svg>
                </div>

                <div className="stat-label">Other Courts</div>
              </div>

              <div className="stat-content">
                <div className="stat-value number-value-stat-comp">
                  {stats.cases.totalOtherCourtCases.toLocaleString()}
                </div>
              </div>
            </div>

            <div className="image-style-stat-card supremecourt-card">
              <div className="head-img">
                <div className="stat-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2L3 7V12C3 16.55 6.84 20.74 12 22C17.16 20.74 21 16.55 21 12V7L12 2Z"
                      fill="currentColor"
                    />
                    <path d="M10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z" fill="white" />
                  </svg>
                </div>

                <div className="stat-label">Supreme Court</div>
              </div>

              <div className="stat-content">
                <div className="stat-value number-value-stat-comp">
                  {(stats.cases.totalSupremeCourtCases || 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderManageCasesContent = () => {
    return (
      <div className="manage-cases-section" ref={casesListRef}>
        <div className="manage-cases-tabs">
          <button
            className={`manage-cases-tab ${
              activeTab === "high-court" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("high-court");

              setActiveSubTab("fetched");
            }}
          >
            High Court
          </button>

          <button
            className={`manage-cases-tab ${
              activeTab === "district-court" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("district-court");

              setActiveSubTab("fetched");
            }}
          >
            District Court
          </button>

          <button
            className={`manage-cases-tab ${
              activeTab === "other-court" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("other-court");

              setActiveSubTab("added");
            }}
          >
            Other Courts
          </button>

          <button
            className={`manage-cases-tab ${
              activeTab === "supreme-court" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("supreme-court");

              setActiveSubTab("fetched");
            }}
          >
            Supreme Court
          </button>
        </div>

        <div>
          <div className="manage-cases-subtabs">
            <div className="sa-search-wrapper">
              <button
                ref={searchButtonRef}
                className={`icon-btn ${showSearchInput ? "active" : ""}`}
                title="Search"
                onClick={handleSearchToggle}
              >
                {showSearchInput ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>

                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                ) : (
                  <FaSearch />
                )}
              </button>

              <button
                className={`icon-btn filter-btn ${
                  hasActiveFilters ? "active-filter" : ""
                }`}
                title="Filter"
                onClick={() => {
                  setShowFilterDropdown(!showFilterDropdown);
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3"></polygon>
                </svg>

                {hasActiveFilters && <span className="filter-indicator" />}
              </button>

              <div
                ref={sasearchContainerRef}
                className="sa-search-input-container"
                style={{ width: 0, opacity: 0, marginLeft: "6px" }}
              >
                <input
                  ref={sasearchInputRef}
                  type="text"
                  value={sasearchQuery}
                  onChange={handleSearchInputChange}
                  onKeyDown={handleKeyPress}
                  placeholder={`Search ${
                    activeTab === "high-court"
                      ? "High Court"
                      : activeTab === "district-court"
                      ? "District Court"
                      : activeTab === "supreme-court"
                      ? "Supreme Court"
                      : "Other Court"
                  } cases...`}
                  className="sa-search-input"
                />

                {isSearching && (
                  <div className="sa-search-loading">
                    <svg
                      className="sa-spinner"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeDasharray="32"
                        strokeDashoffset="32"
                      >
                        <animate
                          attributeName="stroke-dashoffset"
                          values="32;0;32"
                          dur="1s"
                          repeatCount="indefinite"
                        />
                      </circle>
                    </svg>
                  </div>
                )}
              </div>

              {showFilterDropdown && (
                <div className="company-modal-filter-dropdown">
                  <div className="filter-group">
                    <label>Status</label>

                    <select
                      value={filters.status}
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                    >
                      <option value="">All</option>

                      <option value="pending">Pending</option>

                      <option value="disposed">Disposed</option>

                      <option value="appealable">Appealable</option>

                      <option value="re-appealed">Re-Appealed</option>

                      <option value="checked">Checked</option>

                      <option value="notChecked">Unchecked</option>

                      <option value="undated">Undated</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Type</label>

                    <select
                      value={filters.type}
                      onChange={(e) =>
                        handleFilterChange("type", e.target.value)
                      }
                    >
                      <option value="all">All</option>

                      <option value="assigned">Assigned</option>

                      <option value="unassigned">Unassigned</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Filing Date</label>

                    <div className="filter-date-row">
                      <input
                        type="date"
                        value={filters.filingDateFrom}
                        onChange={(e) =>
                          handleFilterChange("filingDateFrom", e.target.value)
                        }
                        placeholder="From"
                      />

                      <input
                        type="date"
                        value={filters.filingDateTo}
                        onChange={(e) =>
                          handleFilterChange("filingDateTo", e.target.value)
                        }
                        placeholder="To"
                      />
                    </div>
                  </div>

                  <div className="filter-group">
                    <label>Registration Date</label>

                    <div className="filter-date-row">
                      <input
                        type="date"
                        value={filters.registrationDateFrom}
                        onChange={(e) =>
                          handleFilterChange(
                            "registrationDateFrom",
                            e.target.value
                          )
                        }
                        placeholder="From"
                      />

                      <input
                        type="date"
                        value={filters.registrationDateTo}
                        onChange={(e) =>
                          handleFilterChange(
                            "registrationDateTo",
                            e.target.value
                          )
                        }
                        placeholder="To"
                      />
                    </div>
                  </div>

                  <div className="filter-group">
                    <label>First Hearing Date</label>

                    <div className="filter-date-row">
                      <input
                        type="date"
                        value={filters.firstHearingDateFrom}
                        onChange={(e) =>
                          handleFilterChange(
                            "firstHearingDateFrom",
                            e.target.value
                          )
                        }
                        placeholder="From"
                      />

                      <input
                        type="date"
                        value={filters.firstHearingDateTo}
                        onChange={(e) =>
                          handleFilterChange(
                            "firstHearingDateTo",
                            e.target.value
                          )
                        }
                        placeholder="To"
                      />
                    </div>
                  </div>

                  <div className="filter-group">
                    <label>Decision Date</label>

                    <div className="filter-date-row">
                      <input
                        type="date"
                        value={filters.decisionDateFrom}
                        onChange={(e) =>
                          handleFilterChange("decisionDateFrom", e.target.value)
                        }
                        placeholder="From"
                      />

                      <input
                        type="date"
                        value={filters.decisionDateTo}
                        onChange={(e) =>
                          handleFilterChange("decisionDateTo", e.target.value)
                        }
                        placeholder="To"
                      />
                    </div>
                  </div>

                  <div className="filter-actions">
                    <button className="filter-clear-btn" onClick={clearFilters}>
                      Clear Filters
                    </button>

                    <button
                      className="filter-close-btn"
                      onClick={() => setShowFilterDropdown(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>

            {(activeTab === "high-court" || activeTab === "district-court" || activeTab === "supreme-court") && (
              <div className="options-courts">
                <button
                  className={`manage-cases-subtab ${
                    activeSubTab === "fetched" ? "active" : ""
                  }`}
                  onClick={() => setActiveSubTab("fetched")}
                >
                  Fetched
                </button>

                <button
                  className={`manage-cases-subtab ${
                    activeSubTab === "added" ? "active" : ""
                  }`}
                  onClick={() => setActiveSubTab("added")}
                >
                  Added
                </button>
              </div>
            )}
          </div>
        </div>

        {SelectionCounter}

        <div className="manage-cases-content">
          {casesLoading ? (
            <div className="manage-cases-loading">
              <div className="manage-cases-spinner"></div>

              <p>Loading cases...</p>
            </div>
          ) : casesError ? (
            <div className="manage-cases-error">
              <p>{casesError}</p>

              <button onClick={fetchCases}>Retry</button>
            </div>
          ) : (
            <div className="manage-cases-list">
              {cases.length === 0 ? (
                <div className="manage-cases-empty">
                  <p>No cases found</p>
                </div>
              ) : (
                cases.map((caseItem, index) => (
                  <ManageCaseRow
                    key={`${caseItem.viewDetails?.courtRef || 'case'}_${index}`}
                    // key={caseItem.viewDetails?.courtRef || index}
                    caseItem={caseItem}
                    index={index}
                    isChecked={selectedRefs.has(caseItem.viewDetails?.courtRef)}
                    isFocused={focusedIndex === index}
                    onCheck={handleCaseCheck}
                    // setRef={(el, idx) => (caseRefs.current[idx] = el)}

                    caseRefs={caseRefs}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="compact-modal-overlay" onClick={onClose}>
      <div
        className="compact-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="compact-modal-header">
          <h3>
            Manage Cases
            {selectedCompany && (
              <span className="company-name">
                {" "}
                - {selectedCompany.companyName}
              </span>
            )}
          </h3>

          <button className="compact-close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        {renderStatsContent()}

        {renderManageCasesContent()}

        <div className="compact-modal-footer">
          {renderPaginationControls()}

          <div className="footer-buttons" style={{ marginLeft: "auto" }}>
            <button
              className="update-cases-btn"
              onClick={handleUpdateCases}
              disabled={updateLoading || !initialLoadComplete}
            >
              {updateLoading ? "Updating..." : getUpdateButtonText()}
            </button>

            <button onClick={onClose}>Close</button>
          </div>
        </div>

        {renderLimitExceededModal()}
      </div>

      <PlanSelectionModal
        isOpen={isPlanModalOpen}
        companyId={companyId}
        onClose={() => setIsPlanModalOpen(false)}
        onSelectPlan={handleSelectPlan}
      />
    </div>
  );
};

export default CompanyLimitModal;